import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileText, Plus, Search, Upload, Edit, Trash2, Eye, ChevronLeft, ChevronRight, CheckCircle, BookOpen, Users, MoreVertical, Image, ImageOff } from 'lucide-react';
import { getImageUrl, getImageUrlSync } from '../../utils/imageUtils';
import AddQuestionModal from './AddQuestionModal';
import BulkUploadModal from './BulkUploadModal';
import EditQuestionModal from './EditQuestionModal';
import ViewQuestionModal from './ViewQuestionModal';
import { CardLoadingSpinner } from '../ui/LoadingSpinner';
import { superAdminAPI } from '../../services/api';
import { showErrorToast, showSuccessToast, showWarningToast } from '../../utils/errorHandling';
import ErrorMessage from '../ui/ErrorMessage';

import { renderLatexForTable } from '../../lib/latex';
import { formatCompactDateTime } from '../../utils/dateUtils';

// Debounce utility function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const QuestionManagement = () => {
  // Refs for table scrolling
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [viewingQuestion, setViewingQuestion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  // Dynamic filter options state - ensure they're always arrays
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableDifficulties, setAvailableDifficulties] = useState([]);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [availableExams, setAvailableExams] = useState([]);
  const [availableQuestionTypes, setAvailableQuestionTypes] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Debounced search term for throttling API requests
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Bulk delete state
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  // Error handling state
  const [error, setError] = useState(null);
  const [operationLoading, setOperationLoading] = useState(false);

  // Use ref to track if it's the initial mount
  const isInitialMount = useRef(true);

  // Load filter options on component mount
  useEffect(() => {
    console.log('Initial mount - loading filter options and questions');
    loadFilterOptions();
    loadQuestions();
  }, []);

  // When filters change, reset to page 0
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip on initial mount
    }
    console.log('Filters changed - resetting to page 0');
    if (currentPage !== 0) {
      setCurrentPage(0);
    } else {
      // Already on page 0, reload immediately
      loadQuestions();
    }
  }, [debouncedSearchTerm, selectedCategory, selectedDifficulty, selectedTenant, selectedExam, selectedQuestionType, selectedTags]);

  // Load questions when pagination changes
  useEffect(() => {
    if (isInitialMount.current) {
      return; // Skip on initial mount
    }
    console.log('Pagination changed - loading questions with page:', currentPage);
    loadQuestions();
  }, [currentPage, pageSize]);

  const loadFilterOptions = async () => {
    try {
      console.log('loadFilterOptions called - loading dynamic filter data from database (database values only for filtering)');

      // Load all filter options from database in parallel
      // Use search endpoints to get only database values (not predefined constants)
      const [
        tenantsResponse,
        subjectsResponse,
        difficultyResponse,
        examNamesResponse,
        questionTypesResponse,
        tagsResponse
      ] = await Promise.all([
        superAdminAPI.getAllTenants(),
        superAdminAPI.searchQuestionSubjects(),
        superAdminAPI.searchQuestionDifficultyLevels(),
        superAdminAPI.searchQuestionExamTypes(),
        superAdminAPI.searchQuestionTypes(),
        superAdminAPI.getAllTags()
      ]);

      // Handle different response structures
      const tenants = tenantsResponse.data?.content || tenantsResponse.data || [];
      const subjects = subjectsResponse.data || [];
      const difficulties = difficultyResponse.data || [];
      const examTypes = examNamesResponse.data || [];
      const questionTypes = questionTypesResponse.data || [];
      const tags = tagsResponse.data || [];

      console.log('Tenants response:', tenants);
      console.log('Subjects response:', subjects);
      console.log('Difficulties response:', difficulties);
      console.log('Exam types response:', examTypes);
      console.log('Question types response:', questionTypes);
      console.log('Tags response:', tags);

      setAvailableTenants(tenants);
      setAvailableSubjects(subjects);
      setAvailableDifficulties(difficulties);
      setAvailableExams(examTypes);
      setAvailableQuestionTypes(questionTypes);
      setAvailableTags(tags);
    } catch (error) {
      console.error('Error loading filter options:', error);
      // Set empty arrays as fallback
      setAvailableTenants([]);
      setAvailableSubjects([]);
      setAvailableDifficulties([]);
      setAvailableExams([]);
      setAvailableQuestionTypes([]);
      setAvailableTags([]);
    }
  };

  const loadQuestions = async () => {
    try {
      console.log('loadQuestions called');
      setLoading(true);
      setError(null); // Clear previous errors

      const params = {
        page: currentPage,
        size: pageSize,
        sortBy: 'createdAt',
        sortDir: 'desc'
      };

      // Add filters - use debounced search term
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (selectedCategory) params.subject = selectedCategory; // Use 'subject' parameter for backend
      if (selectedDifficulty) params.difficulty = selectedDifficulty;
      if (selectedTenant) params.tenantId = selectedTenant;
      if (selectedExam) params.examType = selectedExam;
      if (selectedQuestionType) params.questionType = selectedQuestionType;
      if (selectedTags && selectedTags.length > 0) params.tags = selectedTags.join(',');

      console.log('Making API call with params:', params);
      const response = await superAdminAPI.getAllQuestions(params);
      const data = response.data;
      console.log('API response:', data);

      const questionsArray = data.content || [];
      console.log('Questions array:', questionsArray, 'Is array:', Array.isArray(questionsArray));

      // Ensure we always set an array
      setQuestions(Array.isArray(questionsArray) ? questionsArray : []);
      setTotalElements(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
    } catch (error) {
      console.error('Error loading questions:', error);
      setError('Failed to load questions. Please try again.');
      showErrorToast(error, 'Failed to load questions');

      // Show empty state instead of sample data
      setQuestions([]);
      setTotalElements(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  // Handler functions
  const handleAddQuestion = async (questionData) => {
    try {
      setOperationLoading(true);
      await superAdminAPI.createQuestion(questionData);
      await loadQuestions(); // Reload questions
      showSuccessToast('Question created successfully!');
    } catch (error) {
      console.error('Error creating question:', error);
      showErrorToast(error, 'Failed to create question');
      throw error; // Re-throw to let modal handle it
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setIsEditModalOpen(true);
  };

  const handleViewQuestion = async (question) => {
    // Open immediately with table data, then hydrate with full details.
    setViewingQuestion(question);
    setIsViewModalOpen(true);

    if (!question?.id) return;

    try {
      const response = await superAdminAPI.getQuestionById(question.id);
      if (response?.data) {
        setViewingQuestion(response.data);
      }
    } catch (error) {
      console.error('Error loading question details for view modal:', error);
      showWarningToast(error, 'Showing limited question details');
    }
  };

  const handleUpdateQuestion = async (questionId, questionData) => {
    try {
      setOperationLoading(true);
      const updatedQuestion = await superAdminAPI.updateQuestion(questionId, questionData);
      await loadQuestions(); // Reload questions

      // Update the editing question state with the latest data from server
      // This ensures the modal shows the updated question with new image URLs
      if (editingQuestion && editingQuestion.id === questionId) {
        setEditingQuestion(updatedQuestion.data || updatedQuestion);
      }

      showSuccessToast('Question updated successfully!');
    } catch (error) {
      console.error('Error updating question:', error);
      showErrorToast(error, 'Failed to update question');
      throw error; // Re-throw to let modal handle it
    } finally {
      setOperationLoading(false);
    }
  };

  const handleBulkUpload = async (formData) => {
    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please log in first.');
      }

      // Add default parameters if not provided
      // For cross-tenant questions, don't add tenantIds parameter - let backend handle it as null
      if (!formData.has('tenantIds')) {
        // Don't add tenantIds for cross-tenant questions - backend will set tenantId to null
        console.log('Creating cross-tenant questions (no tenantIds specified)');
      }

      // Remove deprecated kitIds parameter - using tags instead
      if (!formData.has('tags') && !formData.has('kitIds')) {
        formData.append('tags', 'imported'); // Default tag for imported questions
      }

      // Use the Excel import endpoint with authentication
      const response = await fetch('/api/super-admin/questions/import-csv', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        await loadQuestions(); // Reload questions
        alert(`Successfully imported ${result.successCount || 0} questions!`);
        if (result.errorCount > 0) {
          console.warn('Import errors:', result.errors);
          alert(`Note: ${result.errorCount} questions failed to import. Check the console for details.`);
        }
      } else if (response.status === 401) {
        throw new Error('Authentication expired. Please refresh the page and log in again.');
      } else {
        throw new Error(result.message || 'Import failed');
      }
    } catch (error) {
      console.error('Error importing questions:', error);
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('Are you sure you want to delete this question?')) {
      return;
    }

    try {
      setOperationLoading(true);
      await superAdminAPI.deleteQuestion(questionId);
      await loadQuestions(); // Reload questions
      showSuccessToast('Question deleted successfully!');
    } catch (error) {
      console.error('Error deleting question:', error);
      showErrorToast(error, 'Failed to delete question');
    } finally {
      setOperationLoading(false);
    }
  };

  // Bulk delete handlers
  const handleSelectQuestion = (questionId) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
    setIsSelectAllChecked(newSelected.size === questions.length && questions.length > 0);
  };

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedQuestions(new Set());
      setIsSelectAllChecked(false);
    } else {
      const allQuestionIds = new Set(questions.map(q => q.id));
      setSelectedQuestions(allQuestionIds);
      setIsSelectAllChecked(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQuestions.size === 0) {
      alert('Please select questions to delete.');
      return;
    }

    setShowBulkDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const questionIds = Array.from(selectedQuestions);

      // Get authentication token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication required. Please log in first.');
      }

      // Call bulk delete API
      const response = await fetch('/api/super-admin/questions/bulk-delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ questionIds })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        await loadQuestions(); // Reload questions
        setSelectedQuestions(new Set());
        setIsSelectAllChecked(false);
        setShowBulkDeleteConfirm(false);
        alert(`Successfully deleted ${result.deletedCount || questionIds.length} questions!`);
      } else if (response.status === 401) {
        throw new Error('Authentication expired. Please refresh the page and log in again.');
      } else {
        throw new Error(result.message || 'Bulk delete failed');
      }
    } catch (error) {
      console.error('Error deleting questions:', error);
      alert(`Failed to delete questions: ${error.message}`);
    }
  };

  const cancelBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
  };



  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0); // Reset to first page
  };

  // Since we're using server-side pagination and filtering,
  // we don't need client-side filtering
  const filteredQuestions = questions;

  const getTenantLabel = useCallback((question) => {
    if (question?.tenantCodes && question.tenantCodes.length > 0) {
      return question.tenantCodes.join(', ');
    }

    if (question?.tenantId) {
      return availableTenants?.find((tenant) => tenant.id === question.tenantId)?.name || question.tenantId;
    }

    return 'All admin tenants';
  }, [availableTenants]);

  const getAccessScopeLabel = (question) => {
    if (question?.accessScope) {
      return question.accessScope;
    }

    return question?.tenantId || (question?.tenantCodes && question.tenantCodes.length > 0) ? 'TENANT' : 'GLOBAL';
  };

  const getSourceTypeLabel = (question) => {
    if (question?.sourceType === 'ADMIN') return 'Admin';
    return 'Super Admin';
  };

  const getDifficultyBadge = (difficulty) => {
    const baseClasses = "inline-flex items-center px-2 py-1 text-xs font-medium";
    const normalizedDifficulty = difficulty?.toUpperCase();
    switch (normalizedDifficulty) {
      case 'EASY':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'MEDIUM':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'HARD':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-slate-100 text-slate-800`;
    }
  };

  console.log('QuestionManagement render - loading:', loading, 'questions:', questions.length);

  // Helper functions for display
  const getQuestionTypeLabel = (question) => {
    if (question.type) {
      // Normalize legacy MULTIPLE_CHOICE to MCQ SINGLE CORRECT
      if (question.type === 'MULTIPLE_CHOICE') return 'MCQ SINGLE CORRECT';
      return question.type.replace(/_/g, ' ');
    }

    return 'MCQ';
  };

  const getQuestionTypeColor = (question) => {
    const type = question.type || '';
    if (type.includes('MCQ') || type === 'MULTIPLE_CHOICE') return 'bg-blue-100 text-blue-800';
    if (type.includes('TRUE_FALSE')) return 'bg-green-100 text-green-800';
    if (type.includes('FILL')) return 'bg-purple-100 text-purple-800';
    if (type.includes('NUMERIC')) return 'bg-yellow-100 text-yellow-800';
    if (type.includes('ESSAY')) return 'bg-indigo-100 text-indigo-800';
    if (type.includes('MATCHING')) return 'bg-pink-100 text-pink-800';
    if (type.includes('READING_COMPREHENSION') || type.includes('COMPREHENSION') || type.includes('PASSAGE')) return 'bg-teal-100 text-teal-800';
    if (type.includes('LATEX')) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  };

  const hasImages = (question) => {
    return !!(
      question.questionImageUrl ||
      question.explanationImageUrl ||
      (question.options && question.options.some(opt => opt.imageUrl))
    );
  };

  const getImageCount = (question) => {
    let count = 0;
    if (question.questionImageUrl) count++;
    if (question.explanationImageUrl) count++;
    if (question.options) {
      count += question.options.filter(opt => opt.imageUrl).length;
    }
    return count;
  };

  // Component to display image previews in the table
  const ImagePreviewCell = ({ question }) => {
    const [imageUrls, setImageUrls] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadImages = async () => {
        const urls = {};

        // Load question image
        if (question.questionImageUrl) {
          try {
            const url = await getImageUrl(question.questionImageUrl);
            if (url) urls.question = url;
          } catch (error) {
            console.warn('Failed to load question image:', error);
          }
        }

        // Load explanation image
        if (question.explanationImageUrl) {
          try {
            const url = await getImageUrl(question.explanationImageUrl);
            if (url) urls.explanation = url;
          } catch (error) {
            console.warn('Failed to load explanation image:', error);
          }
        }

        // Load option images
        if (question.options) {
          for (let i = 0; i < question.options.length; i++) {
            const option = question.options[i];
            if (option.imageUrl) {
              try {
                const url = await getImageUrl(option.imageUrl);
                if (url) urls[`option_${i}`] = url;
              } catch (error) {
                console.warn(`Failed to load option ${i} image:`, error);
              }
            }
          }
        }

        setImageUrls(urls);
        setLoading(false);
      };

      if (hasImages(question)) {
        loadImages();
      } else {
        setLoading(false);
      }
    }, [question]);

    if (!hasImages(question)) {
      return <ImageOff className="h-3 w-3 text-slate-400" />;
    }

    if (loading) {
      return (
        <div className="flex items-center space-x-1">
          <Image className="h-3 w-3 text-blue-600 animate-pulse" />
          <span className="text-[10px] text-blue-600 font-medium">...</span>
        </div>
      );
    }

    const imageCount = Object.keys(imageUrls).length;
    const totalImages = getImageCount(question);

    if (imageCount === 0) {
      return (
        <div className="flex items-center space-x-1">
          <ImageOff className="h-3 w-3 text-red-400" />
          <span className="text-[10px] text-red-400 font-medium">{totalImages}</span>
        </div>
      );
    }

    // Show first available image as preview
    const firstImageUrl = Object.values(imageUrls)[0];

    return (
      <div className="flex items-center space-x-1">
        <div className="relative">
          <img
            src={firstImageUrl}
            alt="Preview"
            className="h-6 w-6 object-cover rounded border"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <span className="text-[10px] text-blue-600 font-medium">
          {imageCount}/{totalImages}
        </span>
      </div>
    );
  };

  return (
    <div>
      <div className="page-header-compact">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-2xl font-bold azure-text mb-1">Questions</h1>
            <p className="text-sm text-blue-700 font-medium">
              Manage shared question inventory and control which tenants can access each question
            </p>
          </div>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
            {selectedQuestions.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="btn-danger flex items-center"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedQuestions.size})
              </button>
            )}
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="btn-secondary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" />
              Bulk Upload
            </button>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <ErrorMessage
          error={error}
          onClear={() => setError(null)}
          className="mb-6"
        />
      )}



      {/* Compact Filters */}
      <div className="bg-white border border-slate-200 rounded mb-3 p-3">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-slate-900">Filters</h3>
          <div className="text-xs text-slate-600">
            {filteredQuestions.length} of {questions.length} shown
          </div>
        </div>

        {/* First row of filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Subject</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Subjects</option>
              {Array.isArray(availableSubjects) && availableSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Difficulty</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedDifficulty}
              onChange={(e) => setSelectedDifficulty(e.target.value)}
            >
              <option value="">All Difficulties</option>
              {Array.isArray(availableDifficulties) && availableDifficulties.map(difficulty => (
                <option key={difficulty} value={difficulty}>
                  {difficulty.charAt(0).toUpperCase() + difficulty.slice(1).toLowerCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Question Type</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedQuestionType}
              onChange={(e) => setSelectedQuestionType(e.target.value)}
            >
              <option value="">All Types</option>
              {Array.isArray(availableQuestionTypes) && availableQuestionTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
              <input
                type="text"
                placeholder="Search across all fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-6 pr-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Second row of filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Tenant</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
            >
              <option value="">All Tenants</option>
              {Array.isArray(availableTenants) && availableTenants.map(tenant => (
                <option key={tenant.id} value={tenant.id}>{tenant.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Exam</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
            >
              <option value="">All Exams</option>
              {Array.isArray(availableExams) && availableExams.map(exam => (
                <option key={exam} value={exam}>{exam}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Tags</label>
            <select
              className="w-full px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedTags.length > 0 ? selectedTags[0] : ''}
              onChange={(e) => {
                if (e.target.value) {
                  setSelectedTags([e.target.value]);
                } else {
                  setSelectedTags([]);
                }
              }}
            >
              <option value="">All Tags</option>
              {Array.isArray(availableTags) && availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active Filters */}
        {(selectedCategory || selectedDifficulty || selectedTenant || selectedExam || selectedQuestionType || selectedTags.length > 0 || searchTerm) && (
          <div className="flex items-center gap-2 flex-wrap pt-4 border-t border-slate-200">
            <span className="text-sm font-medium text-slate-700">Active filters:</span>
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Subject: {selectedCategory}
                <button onClick={() => setSelectedCategory('')} className="hover:text-blue-900">×</button>
              </span>
            )}
            {selectedDifficulty && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                Difficulty: {selectedDifficulty}
                <button onClick={() => setSelectedDifficulty('')} className="hover:text-yellow-900">×</button>
              </span>
            )}
            {selectedQuestionType && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">
                Type: {selectedQuestionType}
                <button onClick={() => setSelectedQuestionType('')} className="hover:text-indigo-900">×</button>
              </span>
            )}
            {selectedTenant && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Tenant: {availableTenants?.find(t => t.id === selectedTenant)?.name || selectedTenant}
                <button onClick={() => setSelectedTenant('')} className="hover:text-purple-900">×</button>
              </span>
            )}
            {selectedExam && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Exam: {selectedExam}
                <button onClick={() => setSelectedExam('')} className="hover:text-green-900">×</button>
              </span>
            )}
            {selectedTags.length > 0 && selectedTags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                Tag: {tag}
                <button onClick={() => setSelectedTags(selectedTags.filter(t => t !== tag))} className="hover:text-orange-900">×</button>
              </span>
            ))}
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                Search: "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-gray-900">×</button>
              </span>
            )}
            <button
              onClick={() => {
                setSelectedCategory('');
                setSelectedDifficulty('');
                setSelectedTenant('');
                setSelectedExam('');
                setSelectedQuestionType('');
                setSelectedTags([]);
                setSearchTerm('');
              }}
              className="text-xs text-slate-600 hover:text-slate-800 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Questions Table */}
      <div className="card">
        {loading ? (
          <CardLoadingSpinner message="Loading questions..." />
        ) : (
          <div>
            {/* Table Container */}
            <div
              ref={tableScrollRef}
              className="table-container-fixed"
            >
              <table ref={tableRef} className="min-w-full divide-y divide-slate-200 text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-8 sticky left-0 bg-slate-50 shadow-sm z-20">
                    <input
                      type="checkbox"
                      checked={isSelectAllChecked}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                    />
                  </th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-8 sticky left-8 bg-slate-50 shadow-sm z-20"></th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider min-w-[250px]">Question</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden sm:table-cell w-20">Subject</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell w-24">Topic</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell w-16">Type</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell w-16">Images</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell w-16">Diff</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden xl:table-cell w-24">Exams</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden xl:table-cell w-28">Access</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden xl:table-cell w-20">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredQuestions.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="table-cell text-center py-16">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                          {questions.length === 0 ? (
                            <FileText className="h-8 w-8 text-slate-400" />
                          ) : (
                            <Search className="h-8 w-8 text-slate-400" />
                          )}
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 mb-2">
                          {questions.length === 0 ? 'No Questions Yet' : 'No Matching Questions'}
                        </h3>
                        <p className="text-slate-500 mb-6 max-w-sm text-center">
                          {questions.length === 0
                            ? 'Get started by adding your first question or uploading questions in bulk.'
                            : 'Try adjusting your search criteria or filters to find what you\'re looking for.'
                          }
                        </p>
                        {questions.length === 0 && (
                          <div className="flex gap-3">
                            <button
                              onClick={() => setIsAddModalOpen(true)}
                              className="btn-primary flex items-center"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Question
                            </button>
                            <button
                              onClick={() => setIsBulkUploadModalOpen(true)}
                              className="btn-secondary flex items-center"
                            >
                              <Upload className="h-4 w-4 mr-2" />
                              Bulk Upload
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredQuestions.map((question) => (
                <tr key={question.id} className="hover:bg-slate-50">
                  <td className="px-2 py-1 whitespace-nowrap sticky left-0 bg-white shadow-sm z-10">
                    <input
                      type="checkbox"
                      checked={selectedQuestions.has(question.id)}
                      onChange={() => handleSelectQuestion(question.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3"
                    />
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap sticky left-8 bg-white shadow-sm z-10">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleViewQuestion(question)}
                        className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 rounded transition-colors duration-200"
                        title="View Question"
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleEditQuestion(question)}
                        className="text-slate-600 hover:text-slate-900 p-1 hover:bg-slate-100 rounded transition-colors duration-200"
                        title="Edit Question"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleDeleteQuestion(question.id)}
                        className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded transition-colors duration-200"
                        title="Delete Question"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-1 text-xs">
                    <div className="max-w-xs">
                      <div className="text-xs font-medium text-slate-900 leading-tight mb-1 overflow-hidden">
                        <div className="truncate" title={question.questionText || question.text || 'No question text'}>
                          {renderLatexForTable(question.questionText || question.text || 'No question text')}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-[10px] text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                          {question.id?.slice(-6) || 'N/A'}
                        </span>
                        {question.options && question.options.length > 0 && (
                          <span className="text-[10px] text-blue-600 bg-blue-100 px-1 py-0.5 rounded">
                            {question.options.length}opt
                          </span>
                        )}
                        {question.explanation && (
                          <span className="text-[10px] text-green-600 bg-green-100 px-1 py-0.5 rounded">
                            exp
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap text-xs hidden sm:table-cell">
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800 max-w-[80px] truncate"
                      title={question.subject || question.category || 'N/A'}
                    >
                      {question.subject || question.category || 'N/A'}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-xs hidden md:table-cell">
                    <div className="max-w-[120px]">
                      <div
                        className="text-xs text-slate-700 font-medium truncate"
                        title={question.topic || 'N/A'}
                      >
                        {question.topic || 'N/A'}
                      </div>
                      {question.subtopic && (
                        <div
                          className="text-[10px] text-slate-500 truncate"
                          title={question.subtopic}
                        >
                          {question.subtopic}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap text-xs hidden lg:table-cell">
                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${getQuestionTypeColor(question)}`}>
                      {getQuestionTypeLabel(question).substring(0, 3)}
                    </span>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap text-xs hidden lg:table-cell">
                    <div className="flex items-center justify-center">
                      <ImagePreviewCell question={question} />
                    </div>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap text-xs hidden lg:table-cell">
                    <span className={`inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium ${
                      (question.difficultyLevel || question.difficulty || '').toLowerCase() === 'easy' ? 'bg-green-100 text-green-800' :
                      (question.difficultyLevel || question.difficulty || '').toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      (question.difficultyLevel || question.difficulty || '').toLowerCase() === 'hard' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {(question.difficultyLevel || question.difficulty || 'N/A').charAt(0)}
                    </span>
                  </td>

                  <td className="px-2 py-1 text-xs hidden xl:table-cell">
                    <div className="flex flex-wrap gap-0.5 max-w-[100px]">
                      {(question.examTypes && question.examTypes.length > 0) ? (
                        <>
                          {question.examTypes.slice(0, 1).map((type, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 truncate max-w-[60px]"
                              title={type.replace(/_/g, ' ')}
                            >
                              {type.replace(/_/g, '').substring(0, 4)}
                            </span>
                          ))}
                          {question.examTypes.length > 1 && (
                            <span
                              className="text-[10px] text-slate-500 bg-slate-100 px-1 py-0.5 rounded"
                              title={`${question.examTypes.length - 1} more exam types`}
                            >
                              +{question.examTypes.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1 text-xs hidden xl:table-cell">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${getAccessScopeLabel(question) === 'GLOBAL' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}`}>
                        {getAccessScopeLabel(question) === 'GLOBAL' ? 'Global' : 'Tenant'}
                      </span>
                      <div className="text-[10px] text-slate-500">{getSourceTypeLabel(question)}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[120px]" title={getTenantLabel(question)}>
                        {getTenantLabel(question)}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-1 whitespace-nowrap text-xs hidden xl:table-cell">
                    <div className="text-[10px] text-slate-500">
                      {question.createdAt ? (
                        <div>
                          <div>{formatCompactDateTime(question.createdAt).date}</div>
                          <div className="text-[9px] text-slate-400">
                            {formatCompactDateTime(question.createdAt).time}
                          </div>
                        </div>
                      ) : '-'}
                    </div>
                  </td>
                </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Compact Pagination - Always show result count */}
            {!loading && totalElements > 0 && (
              <div className="px-3 py-2 border-t border-slate-200 flex items-center justify-between text-xs">
                <div className="flex items-center space-x-3">
                  <span className="text-slate-600">
                    {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements}
                  </span>
                  <div className="flex items-center space-x-1">
                    <label className="text-slate-600">Show:</label>
                    <select
                      value={pageSize}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-1 py-0.5 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination controls - only show when multiple pages */}
                {totalPages > 1 && (
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                      className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </button>

                    <span className="px-2 py-1 text-xs text-slate-700">
                      {currentPage + 1}/{totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                      className="px-2 py-1 text-xs border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddQuestionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddQuestion}
      />

      <EditQuestionModal
        key={editingQuestion?.id || 'edit-modal'}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingQuestion(null);
        }}
        onSubmit={handleUpdateQuestion}
        question={editingQuestion}
      />

      <BulkUploadModal
        isOpen={isBulkUploadModalOpen}
        onClose={() => setIsBulkUploadModalOpen(false)}
        onSubmit={handleBulkUpload}
      />

      <ViewQuestionModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingQuestion(null);
        }}
        question={viewingQuestion}
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Confirm Bulk Delete
                </h3>
              </div>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete {selectedQuestions.size} selected question{selectedQuestions.size !== 1 ? 's' : ''}?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelBulkDelete}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                className="btn-danger"
              >
                Delete {selectedQuestions.size} Question{selectedQuestions.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionManagement;
